import { PAYMENT_ACCOUNT_TYPES } from './common-constants.js'
import { personName } from './format.js'

export let TRANSACTION_DTYPE = /** @type {const} */ ({
  ChargeTransfer: 'ChargeTransfer',
  DiscountAdjustment: 'DiscountAdjustment',
  DiscountReverseAdjustment: 'DiscountReverseAdjustment',
  FixAdjustment: 'FixAdjustment',
  InstallmentCharge: 'InstallmentCharge',
  LateFeeCharge: 'LateFeeCharge',
  MigrationFixAdjustment: 'MigrationFixAdjustment',
  MiscFeeCharge: 'MiscFeeCharge',
  Payment: 'payment',
  PaymentPlan: 'PaymentPlan',
  PaymentPlanRollback: 'PaymentPlanRollback',
  Refund: 'Refund',
  RetailFeeCharge: 'RetailFeeCharge',
  TransferCharge: 'TransferCharge',
  TxFeeCharge: 'TxFeeCharge',
  UnappliedPayment: 'unapplied_payment',
  WriteOff: 'WriteOff',
})

export let APPLIED_PAYMENT_TYPES = /** @type {const} */ {
  TO_RECEIVABLE: 'TO_RECEIVABLE',
  TO_PAYMENT: 'TO_PAYMENT',
  REFUND: 'REFUND',
  PAYMENT_CORRECTION: 'PAYMENT_CORRECTION',
}

/**
 * Filters available on Transactions and Notes tab in Ledger area.
 */
export let LEDGER_FILTER_TYPE = /** @type {const} */ ({
  ADJUSTMENT: 'ADJUSTMENT',
  APPLIED_PAYMENT: 'APPLIED_PAYMENT',
  CHARGE: 'CHARGE',
  CORRECTION: 'CORRECTION',
  NOTE: 'NOTE',
  PAYMENT: 'PAYMENT',
  PAYMENT_PLAN: 'PAYMENT_PLAN',
  PAYMENT_PLAN_ROLLBACK: 'PAYMENT_PLAN_ROLLBACK',
  REFUND: 'REFUND',
  TRANSFER: 'TRANSFER',
  UNAPPLIED_PAYMENT: 'UNAPPLIED_PAYMENT',
})

/**
 * Entry types as displayed in the Ledger Transactions tab.
 */
export let LEDGER_ENTRY_TYPE = /** @type {const} */ ({
  CHARGE: 'CHARGE',
  INSTALLMENT_CHARGE: 'INSTALLMENT_CHARGE',
  ADJUSTMENT: 'ADJUSTMENT',
  CHARGE_TRANSFER: 'CHARGE_TRANSFER',
  TRANSFER_CHARGE: 'TRANSFER_CHARGE',
  PAYMENT_PLAN: 'PAYMENT_PLAN',
  PAYMENT_PLAN_ROLLBACK: 'PAYMENT_PLAN_ROLLBACK',
  APPLIED_PAYMENT: 'APPLIED_PAYMENT',
  REFUND: 'REFUND',
  PAYMENT_CORRECTION: 'PAYMENT_CORRECTION',
  UNAPPLIED_PAYMENT: 'UNAPPLIED_PAYMENT',
  PAYMENT: 'PAYMENT',
  TRANSFER_CREDIT: 'TRANSFER_CREDIT',
  NOTE: 'NOTE',
})

export let CHARGE_TRANSACTION_DTYPE = /** @type {const} */ ([
  TRANSACTION_DTYPE.LateFeeCharge,
  TRANSACTION_DTYPE.MiscFeeCharge,
  TRANSACTION_DTYPE.RetailFeeCharge,
  TRANSACTION_DTYPE.TxFeeCharge,
])

export let ADJUSTMENT_TRANSACTION_DTYPE = /** @type {const} */ ([
  TRANSACTION_DTYPE.DiscountAdjustment,
  TRANSACTION_DTYPE.DiscountReverseAdjustment,
  TRANSACTION_DTYPE.WriteOff,
  TRANSACTION_DTYPE.FixAdjustment,
  TRANSACTION_DTYPE.MigrationFixAdjustment,
  // This is not the same as a payment refund, this type of transaction is directly added to a receivable
  TRANSACTION_DTYPE.Refund,
])

export let RECEIVABLE_RTYPE = /** @type {const} */ ({
  Invoice: 'Invoice',
  Autopay: 'Autopay',
  Insurance: 'Insurance',
  InitClaim: 'InitClaim',
  ContClaim: 'ContClaim',
  ProcClaim: 'ProcClaim',
})

export let CLAIM_TYPES = /** @type {const} */ [
  RECEIVABLE_RTYPE.Insurance,
  RECEIVABLE_RTYPE.InitClaim,
  RECEIVABLE_RTYPE.ContClaim,
  RECEIVABLE_RTYPE.ProcClaim,
]

export let PAYMENT_SOURCE = /** @type {const} */ ({
  CORE: 'CORE',
  PATIENT_HUB: 'PATIENT_HUB',
  CASE_CLOSER: 'CASE_CLOSER',
  APP_PAY: 'APP_PAY',
})

/**
 *
 * @type {(
 *  payment_account: import('./payments-types.d.ts').PaymentAccount,
 * ) => string}
 */
export function getPayorName(payment_account) {
  if (!payment_account) return '-'

  switch (payment_account.account_type) {
    case PAYMENT_ACCOUNT_TYPES.Person:
      return personName(payment_account.person_payment_account?.payer_person)
    case PAYMENT_ACCOUNT_TYPES.Insurance:
      return (
        payment_account.insurance_payment_account?.insurance_company
          ?.carrier_name ?? '-'
      )
    default:
      return '-'
  }
}

/**
 *
 * @type {(
 * transaction: { applied_payment?: {applied_type:string}, type: string },
 * entity_type: import('./payments-types.d.ts').TransactionFilterTypes) => boolean}
 */
export function isEntityType(transaction, filter_type) {
  switch (filter_type) {
    case LEDGER_FILTER_TYPE.CORRECTION:
      return (
        transaction.applied_payment?.applied_type ===
        APPLIED_PAYMENT_TYPES.PAYMENT_CORRECTION
      )
    case LEDGER_FILTER_TYPE.REFUND:
      return (
        transaction.applied_payment?.applied_type ===
        APPLIED_PAYMENT_TYPES.REFUND
      )
    default:
      return transaction.type === filter_type
  }
}
